import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { tokens } from '../../styles/designTokens';
import HelpButton from './HelpButton';

// Shared modal shell — core/Modal.jsx
//
// Props
//   isOpen           bool          — controls visibility
//   onClose          func          — called when the shell requests close
//   children                       — panel content (caller supplies width/padding)
//   noHelp           bool          — opt out of the HelpButton slot (ErrorReportModal only)
//   closeOnEsc       bool          — default true; pass false for blocking flows (e.g. ResetPassword)
//   closeOnBackdrop  bool|func(e)  — default true; pass false or a fn(MouseEvent)→bool for dynamic guard
//   panelStyle       object        — style override for the panel container
//   panelClassName   string        — className for the panel container
//   alignTop         bool          — flex-start + outer scroll (for tall forms that exceed viewport height)
//
// Shell contract (LAYOUT.md § Modal Shell):
//   - createPortal to document.body — never clipped by ancestor overflow/transform/z-index
//   - tokens.colors.backdrop scrim, tokens.zIndex.modal z-index — no raw literals
//   - ESC-to-close (opt-out via closeOnEsc={false})
//   - Backdrop-click-to-close (opt-out via closeOnBackdrop={false} or a dynamic fn)
//   - Focus moves into panel on open, trapped while open, returned to trigger on close
//   - Body scroll locked while open
//   - HelpButton rendered by default (opt-out via noHelp)
//
// Z-stacking with nested modals: all portals land at document.body at the same
// tokens.zIndex.modal. Child modals are always triggered by state (they mount after
// the parent) so DOM order is always parent-first, child-last — children naturally
// paint on top. No manual z-offsets needed; do not add them.
export default function Modal({
  isOpen,
  onClose,
  children,
  noHelp = false,
  closeOnEsc = true,
  closeOnBackdrop = true,
  panelStyle,
  panelClassName,
  alignTop = false,
}) {
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Store trigger and focus panel on open; restore on close
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      // Defer so the portal has mounted
      const id = setTimeout(() => panelRef.current?.focus(), 0);
      return () => clearTimeout(id);
    } else {
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  // ESC-to-close
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeOnEsc, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prior; };
  }, [isOpen]);

  // Focus trap (Tab / Shift+Tab wraps within the panel)
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const nodes = [...panel.querySelectorAll(FOCUSABLE)].filter(
        (n) => !n.disabled && n.offsetParent !== null
      );
      if (!nodes.length) { e.preventDefault(); return; }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    panel.addEventListener('keydown', handleTab);
    return () => panel.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    const allow = typeof closeOnBackdrop === 'function' ? closeOnBackdrop(e) : closeOnBackdrop;
    if (allow) onClose();
  };

  return createPortal(
    <div
      data-modal-backdrop
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: tokens.colors.backdrop,
        zIndex: tokens.zIndex.modal,
        display: 'flex',
        justifyContent: 'center',
        ...(alignTop
          ? { alignItems: 'flex-start', overflowY: 'auto', padding: '40px 0' }
          : { alignItems: 'center' }),
      }}
    >
      <div
        data-modal-panel
        ref={panelRef}
        tabIndex={-1}
        className={panelClassName}
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', outline: 'none', ...panelStyle }}
      >
        {children}
        {!noHelp && <HelpButton />}
      </div>
    </div>,
    document.body
  );
}
